import {
  Nav,
  NavItem,
  NavList,
  PageSidebar,
  PageSidebarBody,
} from "@patternfly/react-core";
import { MouseEvent as ReactMouseEvent, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import {
  matchPath,
  useHref,
  useLinkClickHandler,
  useLocation,
} from "react-router-dom";
import {
  AccountEnvironment,
  useEnvironment,
} from "@keycloak/keycloak-account-ui";

/* Mirrors the stock PageNav's URL handling (absolute paths derived from
 * environment.baseUrl + matchPath for the active state) but with a fixed
 * menu that adds an "Overview" link back to the Hub landing page. */

const getFullUrl = (path: string, baseUrl: string) =>
  `${new URL(baseUrl).pathname}${path}`;

type NavLinkProps = {
  path: string;
  /** Exact match only — used by Overview so it isn't active on subpages. */
  end?: boolean;
};

const NavLink = ({ path, end, children }: PropsWithChildren<NavLinkProps>) => {
  const { environment } = useEnvironment<AccountEnvironment>();
  const { pathname } = useLocation();
  const fullPath = getFullUrl(path, environment.baseUrl);
  const href = useHref(fullPath);
  const handleClick = useLinkClickHandler(fullPath);
  const isActive = !!matchPath({ path: fullPath, end: end ?? true }, pathname);

  return (
    <NavItem
      data-testid={path || "overview"}
      to={href}
      isActive={isActive}
      onClick={(event) =>
        handleClick(event as unknown as ReactMouseEvent<HTMLAnchorElement>)
      }
    >
      {children}
    </NavItem>
  );
};

export const PageNav = () => {
  const { t } = useTranslation();
  const { environment } = useEnvironment<AccountEnvironment>();
  const features = environment.features;

  return (
    <PageSidebar>
      <PageSidebarBody>
        <Nav>
          <NavList>
            <NavLink path="" end>
              {t("hub.overview", "Overview")}
            </NavLink>
            <NavLink path="personal-info">{t("personalInfo", "Personal info")}</NavLink>
            <NavLink path="account-security/signing-in">
              {t("signingIn", "Signing in")}
            </NavLink>
            <NavLink path="account-security/device-activity">
              {t("deviceActivity", "Device activity")}
            </NavLink>
            {features.isLinkedAccountsEnabled && (
              <NavLink path="account-security/linked-accounts">
                {t("linkedAccounts", "Linked accounts")}
              </NavLink>
            )}
            <NavLink path="applications">{t("applications", "Applications")}</NavLink>
            {features.isViewGroupsEnabled && (
              <NavLink path="groups">{t("groups", "Groups")}</NavLink>
            )}
            {features.isViewOrganizationsEnabled && (
              <NavLink path="organizations">{t("organizations", "Organizations")}</NavLink>
            )}
            {features.isMyResourcesEnabled && (
              <NavLink path="resources">{t("resources", "Resources")}</NavLink>
            )}
            {features.isOid4VciEnabled && (
              <NavLink path="oid4vci">{t("oid4vci", "Credentials")}</NavLink>
            )}
          </NavList>
        </Nav>
      </PageSidebarBody>
    </PageSidebar>
  );
};
